/**
 * Tingbogen bilag are PDF, JPEG, XML, or GML.
 * Only a real PDF can replace the row document. Other types fail by name.
 */

export type UnsupportedBilagReason = "jpeg" | "xml" | "gml" | "other";

export class UnsupportedBilagDocumentError extends Error {
   readonly reason: UnsupportedBilagReason;
   readonly mimeType: string;

   constructor(reason: UnsupportedBilagReason, mimeType: string) {
      super(mimeType || reason);
      this.name = "UnsupportedBilagDocumentError";
      this.reason = reason;
      this.mimeType = mimeType || "unknown";
   }
}

const PDF_MIMES = new Set([
   "application/pdf",
   "application/x-pdf",
   "application/acrobat",
   "application/vnd.pdf",
   "application/vnd.adobe.pdf",
   "text/pdf",
   "text/x-pdf",
   "image/pdf",
]);

const JPEG_MIMES = new Set([
   "image/jpeg",
   "image/jpg",
   "image/pjpeg",
   "image/jpe",
   "image/pjp",
]);

function normalizeMime(value: string): string {
   return value.split(";")[0].trim().toLowerCase();
}

function isPdfMagic(bytes: Uint8Array): boolean {
   let index = 0;
   if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
      index = 3;
   }
   const limit = Math.min(bytes.length, 1024);
   while (index < limit && (bytes[index] === 0x20 || bytes[index] === 0x09 || bytes[index] === 0x0a || bytes[index] === 0x0d)) {
      index += 1;
   }
   return index + 5 <= bytes.length
      && bytes[index] === 0x25
      && bytes[index + 1] === 0x50
      && bytes[index + 2] === 0x44
      && bytes[index + 3] === 0x46
      && bytes[index + 4] === 0x2d;
}

function isJpegMagic(bytes: Uint8Array): boolean {
   return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

function xmlSample(bytes: Uint8Array): string {
   let start = 0;
   if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
      start = 3;
   }
   return new TextDecoder("utf-8", { fatal: false }).decode(bytes.subarray(start, start + 500)).toLowerCase();
}

function looksLikeXml(bytes: Uint8Array): boolean {
   return xmlSample(bytes).trimStart().startsWith("<");
}

function isXmlMime(mime: string): boolean {
   return mime === "application/xml"
      || mime === "text/xml"
      || mime === "application/xhtml+xml"
      || mime.endsWith("+xml");
}

function looksLikeGml(mime: string, bytes: Uint8Array): boolean {
   if (mime.includes("gml")) {
      return true;
   }
   if (!isXmlMime(mime) && !looksLikeXml(bytes)) {
      return false;
   }
   const sample = xmlSample(bytes);
   return sample.includes("gml") || sample.includes("stedfaestelse") || sample.includes("stedfæstelse");
}

function pdfFileName(name: string): string {
   const trimmed = name.trim() || "bilag";
   if (trimmed.toLowerCase().endsWith(".pdf")) {
      return trimmed;
   }
   const dot = trimmed.lastIndexOf(".");
   const stem = dot > 0 ? trimmed.slice(0, dot) : trimmed;
   return `${stem || "bilag"}.pdf`;
}

function reject(reason: UnsupportedBilagReason, mimeType: string): never {
   throw new UnsupportedBilagDocumentError(reason, mimeType);
}

/** Returns a PDF file, or throws when the bilag is not a PDF we can use. */
export async function servitutBilagPdfFile(blob: Blob, name: string): Promise<File> {
   const bytes = new Uint8Array(await blob.arrayBuffer());
   const mime = normalizeMime(blob.type || "");

   if (!bytes.length) {
      reject("other", mime && !PDF_MIMES.has(mime) ? mime : "unknown");
   }
   if (isPdfMagic(bytes)) {
      return new File([bytes], pdfFileName(name), { type: "application/pdf" });
   }
   if (isJpegMagic(bytes) || JPEG_MIMES.has(mime)) {
      reject("jpeg", JPEG_MIMES.has(mime) ? mime : "image/jpeg");
   }
   if (looksLikeGml(mime, bytes)) {
      reject("gml", mime.includes("gml") || isXmlMime(mime) ? mime : "text/xml");
   }
   if (isXmlMime(mime) || looksLikeXml(bytes)) {
      reject("xml", isXmlMime(mime) ? mime : "text/xml");
   }
   reject("other", mime && !PDF_MIMES.has(mime) ? mime : "unknown");
}
